import { Injectable, Inject } from '@angular/core';
import { Analytics, logEvent, isSupported } from '@angular/fire/analytics';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private trackingId: string = environment.firebase.measurementId;

  constructor(@Inject(Analytics) private analytics: Analytics) {
    this.initGtag();
  }

  /**
   * Initializes Google Analytics (gtag.js)
   */
  private initGtag(): void {
    if (!this.trackingId) {
      console.warn('Google Analytics tracking ID is missing.');
      return;
    }

    // If gtag.js is already added, do not append again
    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${this.trackingId}"]`)) {
      return;
    }

    // Load gtag.js dynamically
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.trackingId}`;
    document.head.appendChild(script);

    // Configure gtag function
    const configScript = document.createElement('script');
    configScript.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${this.trackingId}');
    `;
    document.head.appendChild(configScript);
  }

  /**
   * Tracks a page view
   * @param pageTitle Page title
   * @param path URL path
   */
  async trackPageView(pageTitle: string, path: string): Promise<void> {
    if (!(await isSupported())) {
      console.warn('Firebase Analytics is not supported in this environment.');
      return;
    }
    logEvent(this.analytics, 'page_view', {
      page_title: pageTitle,
      page_path: path,
    });
  }

  /**
   * Tracks a custom event
   * @param eventName Name of the event
   * @param params Event parameters
   */
  async trackEvent(eventName: string, params?: Record<string, unknown>): Promise<void> {
    if (!(await isSupported())) {
      console.warn('Firebase Analytics is not supported.');
      return;
    }
    logEvent(this.analytics, eventName, params || {});
  }

  /**
   * Tracks timing of an event
   * @param name Event name
   * @param startTimestamp Start time
   */
  async trackTiming(name: string, startTimestamp: number): Promise<void> {
    if (!(await isSupported())) {
      console.warn('Firebase Analytics is not supported.');
      return;
    }
    const duration = new Date().getTime() - startTimestamp;
    logEvent(this.analytics, 'timing_complete', {
      name,
      value: duration,
    });
  }
}
